import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Moon, Sun, X } from "lucide-react";
import { Logo } from "./Logo";
import { scrollToHash } from "../lib/scroll";
import { TID } from "../lib/testIds";

const LINKS = [
  { to: "/", label: "Home", tid: TID.nav.home },
  { to: "/pricing", label: "Packages", tid: TID.nav.pricing },
  { to: "/contact", label: "Contact", tid: TID.nav.contact },
  { to: "/faq", label: "FAQ", tid: TID.nav.faq },
];

const getTheme = () => {
  try {
    return localStorage.getItem("offcraft-theme") || "light";
  } catch (e) {
    return "light";
  }
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme());
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem("offcraft-theme", next);
    } catch (e) {
      /* private mode */
    }
    document.documentElement.classList.toggle("dark", next === "dark");
  };

  const handleHash = (e, to) => {
    const [path, hash] = to.split("#");
    if (!hash || location.pathname !== path) return;
    e.preventDefault();
    scrollToHash(hash);
  };

  const themeButton = (tid) => (
    <button
      data-testid={tid}
      onClick={toggleTheme}
      aria-label="Toggle dark mode"
      className="flex h-10 w-10 items-center justify-center border border-ink/15 text-ink transition-colors hover:border-crimson/60 hover:text-crimson"
    >
      {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );

  return (
    <header className="fixed inset-x-0 top-0 z-40 border-b border-ink/10 bg-paper/90 backdrop-blur-xl">
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6 lg:h-20">
        <Link to="/" data-testid="nav-logo" aria-label="OFFCRAFT home">
          <Logo />
        </Link>
        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.tid}
              onClick={(e) => handleHash(e, l.to)}
              className={({ isActive }) =>
                `font-body text-[13px] font-bold uppercase tracking-[0.12em] transition-colors ${
                  isActive && !l.hash
                    ? "text-crimson underline decoration-2 underline-offset-8"
                    : "text-ink hover:text-crimson"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {themeButton("nav-theme-toggle")}
          <Link
            to="/contact"
            data-testid={TID.nav.cta}
            className="bg-crimson px-6 py-3 font-body text-[13px] font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-crimsondark"
          >
            Start a project
          </Link>
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          {themeButton("nav-theme-toggle-mobile")}
          <button
            data-testid={TID.nav.mobileToggle}
            className="flex h-10 w-10 items-center justify-center border border-ink/15 text-ink"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-b border-ink/10 bg-paper/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={l.tid}
                  onClick={(e) => handleHash(e, l.to)}
                  className={({ isActive }) =>
                    `block py-3 font-display text-2xl font-bold ${isActive && !l.hash ? "text-crimson" : "text-ink"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" data-testid={TID.nav.cta} className="mt-4 inline-flex bg-crimson px-6 py-3 font-display text-sm font-bold text-white">
                Start a project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
